// The in-memory API's team KPIs (TEAM-KPI fake twin, founder-approved 2026-10-21): `GET/PUT/DELETE
// teams/{id}/metrics[/{metric_id}]`, `GET teams/{id}/scorecard` and `GET scorecards/teams`, at
// parity with services/api/src/routes/team-kpis.ts and kpi/team.ts. The rules are the domain's
// (`combineMembers`, `metricHealth`, `attainment`, `scoreOwnMetrics`, `finalBand`):
//   - `sum` / `average`: the team's member seats' values (the structure fake's seat scorecards,
//     so a number entered on a seat moves its team), a seat without a value left out;
//   - `none`: the team's own observations (the shared store; a lead or manager enters them), an
//     event metric its members' values added up;
//   - target: the team's own rule, else the company's; health: the metric's bands, else the
//     company's.
// Reads are `team#viewer` (managers, the lead, the human owner, the member seats' holders; 404
// otherwise); writes the lead's or a manager's (403). The structure fake owns the stores.
import {
  attainment,
  combineMembers,
  DEFAULTS,
  finalBand,
  type MemberValue,
  metricHealth,
  propagate,
  scoreOwnMetrics,
} from "@companyos/domain-kpi";
import type { OperationOutput, Problem, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { COMPANY_IDS, type FakeCompany } from "./fake-data";
import { observationRow } from "./fake-entry";
import type { KpiStanding } from "./fake-kpi-visibility";
import type { MetricDef, Observation, Scorecard, Seat, SeatAssignment, TargetRule } from "./types";

type TeamRow = Omit<OperationOutput<"teams.get">, "members">;
type TeamMember = OperationOutput<"teams.get">["members"][number];
type TeamMetricLine = OperationOutput<"teams.metrics">["data"][number];
type TeamScore = OperationOutput<"scorecards.teams">["data"][number];
type Line = Scorecard["metrics"][number];
type Rollup = TeamMetricLine["rollup"];
type Stored = {
  team_id: string;
  metric_def_id: string;
  rollup: Rollup;
  kpi_weight: string;
  position: number;
  created_at: string;
  updated_at: string;
};

export type TeamKpiDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: Problem["code"], title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  now: () => Date;
  nextSeq: () => number;
  todayWeek: () => string;
  weekParam: (url: URL, name: string) => string | Response;
  metricsOf: (company: FakeCompany) => MetricDef[];
  targetsOf: (company: FakeCompany) => TargetRule[];
  seatsOf: (company: FakeCompany) => Seat[];
  assignmentsOf: (company: FakeCompany) => SeatAssignment[];
  teamsOf: (company: FakeCompany) => TeamRow[];
  membersOf: (company: FakeCompany) => TeamMember[];
  observations: Observation[];
  /** domain-kpi `liveOf` over the fake's rows (the structure fake's). */
  liveOf: (rows: Observation[]) => Observation | null;
  /** A seat's scorecard for the week (the structure fake's, entered numbers applied). */
  seatCard: (p: Principal, seatId: string, week: string) => Scorecard | null;
  /** KPI-VISIBILITY: the caller's KPI standing (a team whose lead seat it reads is readable). */
  kpiStanding?: (p: Principal) => KpiStanding;
  newId: (n: number) => string;
};

const CAP = DEFAULTS.attainment_cap;
const FIRST_DAY = "2000-01-01";

/** `2026-W41` → that ISO week's Monday (UTC). */
function weekMonday(week: string): Date {
  const [y, w] = week.split("-W").map(Number) as [number, number];
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const monday = new Date(jan4.getTime() - ((jan4.getUTCDay() + 6) % 7) * 86_400_000);
  return new Date(monday.getTime() + (w - 1) * 7 * 86_400_000);
}
function shift(week: string, by: number): string {
  const d = new Date(weekMonday(week).getTime() + by * 7 * 86_400_000 + 3 * 86_400_000);
  const day = (d.getUTCDay() + 6) % 7;
  const thursday = new Date(d.getTime() - (day - 3) * 86_400_000);
  const year = thursday.getUTCFullYear();
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const firstThu = new Date(jan4.getTime() - (((jan4.getUTCDay() + 6) % 7) - 3) * 86_400_000);
  const n = Math.round((thursday.getTime() - firstThu.getTime()) / (7 * 86_400_000)) + 1;
  return `${year}-W${String(n).padStart(2, "0")}`;
}

export function teamKpiHandlers(d: TeamKpiDeps): Partial<Record<RouteId, Handler>> {
  const { json, problem, notFound, validation } = d;
  const forbidden = (detail?: string) => problem(403, "forbidden", "Forbidden", detail);
  const store = new Map<string, Stored[]>();
  const stamp = () => d.now().toISOString();
  const actor = (p: Principal) => ({
    actor_kind: p.operator ? ("agency_user" as const) : ("person" as const),
    actor_membership_id: null,
  });

  const linesOf = (company: FakeCompany) => {
    let list = store.get(company.id);
    if (!list) {
      list = [];
      store.set(company.id, list);
      if (company.id === COMPANY_IDS.acme) seed(company, list);
    }
    return list;
  };

  /**
   * The demo's team KPIs, once, on Acme's first seeded team: its members' Candidates sourced summed, their
   * approval rate averaged, and the team's own Days to shortlist (lower is better, its own bands:
   * green from 100%, amber from 85%) with six weeks of entered values.
   */
  function seed(company: FakeCompany, list: Stored[]) {
    const team = d.teamsOf(company)[0];
    const defs = d.metricsOf(company);
    const sourced = defs.find((m) => m.key === "de.candidates_sourced");
    const approval = defs.find((m) => m.key === "de.approval_rate");
    if (!team || !sourced || !approval) return;
    const at = stamp();
    const own: MetricDef = {
      company_id: company.id,
      id: d.newId(0x7e4100),
      key: "team.days_to_shortlist",
      name: "Days to shortlist",
      description: "Working days from a role's intake to the shortlist sent to the client.",
      unit: "number",
      direction: "lte",
      aggregation: "average",
      normalization: null,
      cadence: "weekly",
      source: "manual",
      event_query: null,
      formula: null,
      task_query: null,
      visibility: "client",
      gap_policy: "excuse",
      archived_at: null,
      needs_review: false,
      bands: { green: 100, amber: 85 },
      gate: null,
      actor_kind: "system",
      actor_membership_id: null,
      created_at: at,
      updated_at: at,
    };
    defs.push(own);
    const line = (metric: MetricDef, rollup: Rollup, position: number): Stored => ({
      team_id: team.id,
      metric_def_id: metric.id,
      rollup,
      kpi_weight: "1",
      position,
      created_at: at,
      updated_at: at,
    });
    list.push(line(sourced, "sum", 0), line(approval, "average", 1), line(own, "none", 2));
    const rule = (metric: MetricDef, i: number, value: string): TargetRule => ({
      company_id: company.id,
      id: d.newId(0x7e4101 + i),
      metric_def_id: metric.id,
      subject_kind: "team",
      subject_id: team.id,
      period_type: "week",
      value,
      value_max: null,
      effective_from: FIRST_DAY,
      effective_to: null,
      actor_kind: "system",
      actor_membership_id: null,
      created_at: at,
      updated_at: at,
    });
    d.targetsOf(company).push(rule(sourced, 0, "80"), rule(own, 1, "5"));
    // This week amber (5.5 days: 91% of target), then a mix of on-target and late weeks.
    const values = [5.5, 4.5, 6.5, 5, 4, 6];
    d.observations.push(
      ...values.map((value, i) =>
        observationRow(company.id, 0x7e4000 + i, {
          metric_def_id: own.id,
          period_type: "week",
          period_key: shift(d.todayWeek(), -i),
          value,
          note: null,
          subject: { kind: "team", id: team.id },
          created_at: at,
        }),
      ),
    );
  }

  const visibleMetrics = (p: Principal) =>
    d.metricsOf(p.company).filter((m) => m.visibility === "client" || p.operator);

  /** The caller's active seats. */
  const heldBy = (p: Principal) =>
    new Set(
      d
        .assignmentsOf(p.company)
        .filter(
          (a) =>
            a.active_to === null &&
            p.me.memberships.some((m) => m.membership_id === a.membership_id),
        )
        .map((a) => a.seat_id),
    );

  /** 05 `team#viewer` (the teams fake's rule) and whether the caller writes its KPIs. */
  function access(p: Principal, id: string | undefined) {
    const t = d.teamsOf(p.company).find((x) => x.id === id);
    if (!t) return null;
    if (p.manager) return { t, canEdit: true };
    const held = heldBy(p);
    const lead = Boolean(t.lead_seat_id && held.has(t.lead_seat_id));
    const sees =
      lead ||
      Boolean(t.human_owner_seat_id && held.has(t.human_owner_seat_id)) ||
      d.membersOf(p.company).some((m) => m.team_id === t.id && held.has(m.seat_id));
    if (sees) return { t, canEdit: lead };
    // KPI-VISIBILITY (the API's `teamFor`): the lead seat's KPIs open the team for reading.
    if (t.lead_seat_id && d.kpiStanding?.(p).sees("kpi", t.lead_seat_id))
      return { t, canEdit: false };
    return null;
  }

  const members = (p: Principal, teamId: string) => {
    const open = new Set(
      d
        .seatsOf(p.company)
        .filter((s) => s.status !== "closed")
        .map((s) => s.id),
    );
    return d
      .membersOf(p.company)
      .filter((m) => m.team_id === teamId && open.has(m.seat_id))
      .map((m) => m.seat_id)
      .sort();
  };

  /** The team's own live weekly value of a metric (its observations). */
  function ownValue(p: Principal, teamId: string, def: MetricDef, week: string): MemberValue {
    const cell = d.observations.filter(
      (o) =>
        o.company_id === p.company.id &&
        o.metric_def_id === def.id &&
        o.subject_kind === "team" &&
        o.subject_id === teamId &&
        o.period_type === "week" &&
        o.period_key === week,
    );
    const live = cell.length ? d.liveOf(cell) : null;
    const v = live?.value === null || live?.value === undefined ? null : Number(live.value);
    return {
      actual: v,
      expected: 1,
      present: v === null ? 0 : 1,
      completeness: v === null ? 0 : 1,
    };
  }

  function memberValue(p: Principal, seatId: string, def: MetricDef, week: string): MemberValue {
    const line = d.seatCard(p, seatId, week)?.metrics.find((m) => m.metric_def_id === def.id);
    const actual = line?.actual ?? null;
    return {
      actual,
      expected: 1,
      present: actual === null ? 0 : 1,
      completeness: actual === null ? 0 : 1,
    };
  }

  function lineValue(p: Principal, l: Stored, def: MetricDef, week: string): MemberValue {
    const seats = members(p, l.team_id);
    if (l.rollup === "sum" || l.rollup === "average")
      return combineMembers(
        l.rollup,
        seats.map((s) => memberValue(p, s, def, week)),
      );
    if (def.source === "event")
      return combineMembers(
        "sum",
        seats.map((s) => memberValue(p, s, def, week)),
      );
    return ownValue(p, l.team_id, def, week);
  }

  /** The team's weekly rule in force on the week's Monday, else the company's. */
  function targetOf(p: Principal, teamId: string, def: MetricDef, week: string) {
    const day = weekMonday(week).toISOString().slice(0, 10);
    const on = (t: TargetRule) =>
      t.metric_def_id === def.id &&
      t.period_type === "week" &&
      t.effective_from <= day &&
      (t.effective_to === null || t.effective_to >= day);
    const rules = d.targetsOf(p.company).filter(on);
    const rule =
      rules.find((t) => t.subject_kind === "team" && t.subject_id === teamId) ??
      rules.find((t) => t.subject_kind === "company");
    return rule
      ? { target: Number(rule.value), max: rule.value_max === null ? null : Number(rule.value_max) }
      : { target: null, max: null };
  }

  function scorecard(p: Principal, t: TeamRow, week: string): Scorecard {
    const defs = new Map(visibleMetrics(p).map((m) => [m.id, m]));
    const lines = linesOf(p.company)
      .filter((l) => l.team_id === t.id && defs.has(l.metric_def_id))
      .sort((a, b) => a.position - b.position || a.metric_def_id.localeCompare(b.metric_def_id));
    const metrics: Line[] = [];
    for (const l of lines) {
      const def = defs.get(l.metric_def_id) as MetricDef;
      const v = lineValue(p, l, def, week);
      const { target, max } = targetOf(p, t.id, def, week);
      const att =
        v.actual !== null && target !== null
          ? attainment(def.direction, v.actual, target, CAP, max)
          : null;
      const weight = Number(l.kpi_weight);
      metrics.push({
        metric_def_id: def.id,
        key: def.key,
        name: def.name,
        unit: def.unit,
        direction: def.direction,
        visibility: def.visibility,
        actual: v.actual,
        target,
        target_max: max,
        attainment: att,
        bands: def.bands ?? null,
        health: metricHealth(att, def.bands),
        expected: v.expected,
        present: v.present,
        completeness: v.completeness,
        tracking_mode: "scored",
        state:
          target === null
            ? "unmeasured"
            : v.actual === null
              ? "missing"
              : v.completeness < 1
                ? "provisional"
                : "complete",
        kpi_weight: weight,
        goal_weight: weight,
        trend: Array.from({ length: 13 }, (_, i) => shift(week, i - 12)).map((w) => ({
          period_key: w,
          actual: lineValue(p, l, def, w).actual,
          target: targetOf(p, t.id, def, w).target,
        })),
        rollup: l.rollup,
      });
    }
    const inputs = metrics.map((m) => ({
      metricId: m.metric_def_id,
      name: m.name,
      direction: m.direction,
      kpiWeight: m.kpi_weight,
      goalWeight: m.goal_weight,
      actual: m.actual,
      target: m.target,
      targetMax: m.target_max,
      expected: m.expected,
      completeness: m.completeness,
    }));
    const s = scoreOwnMetrics(inputs, CAP);
    const start = weekMonday(week);
    const end = new Date(start.getTime() + 7 * 86_400_000 - 1000);
    return {
      subject: { kind: "team", id: t.id, name: t.name },
      period: {
        type: "week",
        key: week,
        starts_at: start.toISOString(),
        ends_at: `${end.toISOString().slice(0, 19)}Z`,
      },
      computed_at: stamp(),
      kpi_score: s.kpiScore,
      goal_score: s.goalScore,
      completeness: s.completeness,
      band: finalBand(
        s.kpiScore,
        s.completeness,
        DEFAULTS.insufficient_data_cutoff,
        DEFAULTS.kpi_thresholds,
      ),
      metrics,
      contributors: propagate(s, inputs, 1, [], CAP).contributors,
      cost: null,
      hours: null,
    };
  }

  function lineOut(p: Principal, l: Stored): TeamMetricLine | null {
    const def = visibleMetrics(p).find((m) => m.id === l.metric_def_id);
    if (!def) return null;
    const today = d.now().toISOString().slice(0, 10);
    const rule = d
      .targetsOf(p.company)
      .filter(
        (t) =>
          t.metric_def_id === def.id &&
          t.subject_kind === "team" &&
          t.subject_id === l.team_id &&
          t.effective_from <= today &&
          (t.effective_to === null || t.effective_to >= today),
      )
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
    return {
      company_id: p.company.id,
      team_id: l.team_id,
      metric_def_id: def.id,
      rollup: l.rollup,
      kpi_weight: l.kpi_weight,
      position: l.position,
      ...actor(p),
      created_at: l.created_at,
      updated_at: l.updated_at,
      metric_key: def.key,
      metric_name: def.name,
      unit: def.unit,
      direction: def.direction,
      source: def.source,
      cadence: def.cadence,
      bands: def.bands ?? null,
      target: rule
        ? {
            period_type: rule.period_type,
            value: Number(rule.value),
            value_max: rule.value_max === null ? null : Number(rule.value_max),
            effective_from: rule.effective_from,
          }
        : null,
    };
  }

  /** The team's target from the API's dating rule (first: its history; later: this week on). */
  function setTarget(
    p: Principal,
    teamId: string,
    metricId: string,
    target: { period_type: string; value: unknown; value_max?: unknown } | null,
  ) {
    const all = d.targetsOf(p.company);
    const mine = all.filter(
      (t) => t.metric_def_id === metricId && t.subject_kind === "team" && t.subject_id === teamId,
    );
    const from =
      mine.length === 0 ? FIRST_DAY : weekMonday(d.todayWeek()).toISOString().slice(0, 10);
    const before = new Date(Date.parse(`${from}T12:00:00Z`) - 86_400_000)
      .toISOString()
      .slice(0, 10);
    for (const r of mine.filter((t) => t.effective_to === null || t.effective_to >= from)) {
      if (r.effective_from >= from) all.splice(all.indexOf(r), 1);
      else r.effective_to = before;
    }
    if (!target) return;
    const at = stamp();
    all.push({
      company_id: p.company.id,
      id: d.newId(d.nextSeq()),
      metric_def_id: metricId,
      subject_kind: "team",
      subject_id: teamId,
      period_type: target.period_type as TargetRule["period_type"],
      value: String(Number(target.value)),
      value_max: target.value_max == null ? null : String(Number(target.value_max)),
      effective_from: from,
      effective_to: null,
      ...actor(p),
      created_at: at,
      updated_at: at,
    });
  }

  const WEIGHT = (v: unknown) => {
    const n = Number(v);
    return typeof v === "string" && v.trim() !== "" && n >= 0 && n <= 999.999 ? n : null;
  };

  return {
    "teams.metrics": ({ principal, params }) => {
      if (!principal) return notFound();
      const a = access(principal, params.id);
      if (!a) return notFound();
      const data = linesOf(principal.company)
        .filter((l) => l.team_id === a.t.id)
        .sort((x, y) => x.position - y.position || x.metric_def_id.localeCompare(y.metric_def_id))
        .flatMap((l) => {
          const out = lineOut(principal, l);
          return out ? [out] : [];
        });
      return json({ data, can_edit: a.canEdit });
    },
    "teams.putMetric": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const a = access(principal, params.id);
      if (!a) return notFound();
      if (!a.canEdit) return forbidden("a team's KPIs are set by its lead or a company manager");
      const def = visibleMetrics(principal).find((m) => m.id === params.metric_id);
      if (!def) return notFound();
      if (def.archived_at) return validation("metric_id: the metric is archived");
      if (!def.cadence) return validation("metric_id: a team KPI needs a metric with a cadence");
      const b = (body ?? {}) as Record<string, unknown>;
      if (!["none", "sum", "average"].includes(String(b.rollup))) return validation("rollup");
      const weight = WEIGHT(b.kpi_weight);
      if (weight === null) return validation("kpi_weight: 0 to 999.999");
      const list = linesOf(principal.company);
      const mine = list.filter((l) => l.team_id === a.t.id);
      const existing = mine.find((l) => l.metric_def_id === def.id);
      const position =
        typeof b.position === "number"
          ? b.position
          : (existing?.position ?? mine.reduce((m, l) => Math.max(m, l.position + 1), 0));
      const at = stamp();
      const row: Stored = {
        team_id: a.t.id,
        metric_def_id: def.id,
        rollup: b.rollup as Rollup,
        kpi_weight: String(weight),
        position,
        created_at: existing?.created_at ?? at,
        updated_at: at,
      };
      if (existing) list.splice(list.indexOf(existing), 1, row);
      else list.push(row);
      if (b.target !== undefined) {
        const t = b.target as {
          period_type?: unknown;
          value?: unknown;
          value_max?: unknown;
        } | null;
        if (t !== null && (typeof t.period_type !== "string" || !Number.isFinite(Number(t.value))))
          return validation("target: period_type and value");
        setTarget(principal, a.t.id, def.id, t as never);
      }
      return json(lineOut(principal, row));
    },
    "teams.removeMetric": ({ principal, params }) => {
      if (!principal) return notFound();
      const a = access(principal, params.id);
      if (!a) return notFound();
      if (!a.canEdit) return forbidden("a team's KPIs are set by its lead or a company manager");
      const hidden = d
        .metricsOf(principal.company)
        .some((m) => m.id === params.metric_id && m.visibility === "agency" && !principal.operator);
      if (hidden) return notFound();
      const list = linesOf(principal.company);
      const i = list.findIndex((l) => l.team_id === a.t.id && l.metric_def_id === params.metric_id);
      if (i >= 0) list.splice(i, 1);
      return new Response(null, { status: 204 });
    },
    "teams.scorecard": ({ principal, params, url }) => {
      if (!principal) return notFound();
      const a = access(principal, params.id);
      if (!a) return notFound();
      const week = d.weekParam(url, "period");
      if (week instanceof Response) return week;
      return json(scorecard(principal, a.t, week));
    },
    "scorecards.teams": ({ principal, url }) => {
      if (!principal) return notFound();
      const week = d.weekParam(url, "period");
      if (week instanceof Response) return week;
      const withKpis = new Set(linesOf(principal.company).map((l) => l.team_id));
      const data: TeamScore[] = d
        .teamsOf(principal.company)
        .filter((t) => withKpis.has(t.id) && access(principal, t.id))
        .sort((x, y) => x.name.localeCompare(y.name))
        .map((t) => {
          const sc = scorecard(principal, t, week);
          const count = (h: "green" | "amber" | "red") =>
            sc.metrics.filter((m) => m.health === h).length;
          return {
            team_id: t.id,
            name: t.name,
            kpi_score: sc.kpi_score,
            goal_score: sc.goal_score,
            completeness: sc.completeness,
            band: sc.band,
            metrics: sc.metrics.length,
            health: { green: count("green"), amber: count("amber"), red: count("red") },
          };
        });
      return json({ data });
    },
  };
}

/** Whether `p` may enter team `teamId`'s own value of `metricId` (its lead, or a manager). */
export function teamEntryAllowed(
  p: Principal,
  team: TeamRow | undefined,
  assignments: SeatAssignment[],
): boolean {
  if (!team) return false;
  if (p.manager) return true;
  return assignments.some(
    (a) =>
      a.active_to === null &&
      a.seat_id === team.lead_seat_id &&
      p.me.memberships.some((m) => m.membership_id === a.membership_id),
  );
}
